import styled from "styled-components";
import { Drawer, Chip } from "@material-ui/core";

// ─── Shell ───────────────────────────────────────────────────────────────────

export const WorkspaceWrap = styled.div`
	padding: 24px 0 48px;
	min-height: 100vh;
	background: radial-gradient(circle at top left, rgba(214, 227, 255, 0.76), transparent 26%),
		radial-gradient(circle at top right, rgba(255, 227, 214, 0.38), transparent 24%),
		linear-gradient(180deg, #f8f7fc 0%, #eef2f8 100%);

	@media (max-width: 640px) {
		padding: 14px 0 28px;
	}
`;

export const WorkspaceInner = styled.div`
	width: min(1760px, calc(100vw - 36px));
	margin: 0 auto;

	@media (max-width: 1400px) {
		width: 100%;
		padding: 0 16px;
	}

	@media (max-width: 640px) {
		padding: 0 10px;
	}
`;

export const WorkspaceGrid = styled.div<{ detectionOpen?: boolean }>`
	display: grid;
	grid-template-columns: ${({ detectionOpen }) => (detectionOpen ? "260px minmax(0,1fr)" : "minmax(0,1fr)")};
	gap: 20px;
	align-items: start;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;

export const WorkspaceMain = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

// ─── Scope Switcher ───────────────────────────────────────────────────────────

export const ScopeSwitcherWrap = styled.div`
	display: inline-flex;
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	overflow: hidden;
	background: #fff;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

export const ScopeTab = styled.button<{ active?: boolean }>`
	padding: 8px 20px;
	border: none;
	background: ${({ active }) => (active ? "#2563eb" : "transparent")};
	color: ${({ active }) => (active ? "#fff" : "#475569")};
	font-size: 0.875rem;
	font-weight: ${({ active }) => (active ? 600 : 400)};
	cursor: pointer;
	transition: background 0.15s, color 0.15s;
	white-space: nowrap;

	&:hover {
		background: ${({ active }) => (active ? "#1d4ed8" : "#f1f5f9")};
	}
`;

// ─── Toolbar ─────────────────────────────────────────────────────────────────

export const ToolbarWrap = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	align-items: center;
	padding: 12px 16px;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

export const ToolbarSection = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
	flex-wrap: wrap;
`;

export const ToolbarSpacer = styled.div`
	flex: 1;
`;

export const ToolbarIconBtn = styled.button<{ active?: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 7px 12px;
	border: 1px solid ${({ active }) => (active ? "#2563eb" : "#e2e8f0")};
	border-radius: 8px;
	background: ${({ active }) => (active ? "#eff6ff" : "#fff")};
	color: ${({ active }) => (active ? "#2563eb" : "#475569")};
	font-size: 0.8125rem;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s;
	white-space: nowrap;

	&:hover {
		background: #f1f5f9;
		border-color: #94a3b8;
	}

	i {
		font-size: 0.875rem;
	}
`;

export const ToolbarPrimaryBtn = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 7px 16px;
	border: none;
	border-radius: 8px;
	background: #2563eb;
	color: #fff;
	font-size: 0.8125rem;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s;
	white-space: nowrap;

	&:hover {
		background: #1d4ed8;
	}

	&:disabled {
		background: #94a3b8;
		cursor: not-allowed;
	}
`;

export const ToolbarSecondaryBtn = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 7px 14px;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	background: #fff;
	color: #374151;
	font-size: 0.8125rem;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s;
	white-space: nowrap;

	&:hover {
		background: #f8fafc;
		border-color: #94a3b8;
	}
`;

// ─── Table ───────────────────────────────────────────────────────────────────

export const TableWrap = styled.div`
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

export const TableScrollBox = styled.div`
	overflow-x: auto;
	max-height: calc(100vh - 280px);
	overflow-y: auto;
`;

export const StyledTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 0.8125rem;
`;

export const StyledTHead = styled.thead`
	background: #f8fafc;
	position: sticky;
	top: 0;
	z-index: 2;
`;

export const StyledTh = styled.th<{ sortable?: boolean; width?: string }>`
	padding: 10px 12px;
	text-align: left;
	font-weight: 600;
	color: #475569;
	border-bottom: 1px solid #e2e8f0;
	white-space: nowrap;
	width: ${({ width }) => width || "auto"};
	cursor: ${({ sortable }) => (sortable ? "pointer" : "default")};
	user-select: none;

	&:hover {
		color: ${({ sortable }) => (sortable ? "#2563eb" : "inherit")};
	}
`;

export const StyledTBody = styled.tbody``;

export const StyledTr = styled.tr<{ selected?: boolean; active?: boolean }>`
	border-bottom: 1px solid #f1f5f9;
	background: ${({ selected, active }) => (active ? "#eff6ff" : selected ? "#f8fafc" : "#fff")};
	transition: background 0.1s;

	&:hover {
		background: #f8fafc;
	}

	&:last-child {
		border-bottom: none;
	}
`;

export const StyledTd = styled.td`
	padding: 8px 12px;
	vertical-align: middle;
	color: #1e293b;
`;

export const KeyLabel = styled.span`
	font-family: "Fira Code", "Courier New", monospace;
	font-size: 0.75rem;
	color: #475569;
	background: #f1f5f9;
	padding: 2px 6px;
	border-radius: 4px;
	white-space: nowrap;
	max-width: 220px;
	display: inline-block;
	overflow: hidden;
	text-overflow: ellipsis;
	vertical-align: middle;
`;

export const StatusChip = styled.span<{ status: "draft" | "published" }>`
	display: inline-block;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 0.6875rem;
	font-weight: 600;
	background: ${({ status }) => (status === "published" ? "#dcfce7" : "#fef9c3")};
	color: ${({ status }) => (status === "published" ? "#166534" : "#854d0e")};
`;

export const SortIcon = styled.span`
	margin-left: 4px;
	opacity: 0.6;
	font-size: 0.75rem;
`;

export const EmptyTextHint = styled.span`
	display: inline-block;
	margin-left: 8px;
	padding: 1px 6px;
	border-radius: 6px;
	background: #fef3c7;
	color: #92400e;
	font-size: 0.625rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	vertical-align: middle;
`;

// ─── Page Header ──────────────────────────────────────────────────────────────

export const PageHeader = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 12px;
	padding: 14px 18px;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

export const PageTitleGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

export const PageTitle = styled.h1`
	font-size: 1.25rem;
	font-weight: 700;
	color: #0f172a;
	margin: 0;
	letter-spacing: -0.01em;
`;

export const PageSubtitle = styled.div`
	font-size: 0.8125rem;
	color: #64748b;
`;

export const PageStats = styled.div`
	display: flex;
	gap: 18px;
	font-size: 0.8125rem;
`;

export const PageStat = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
`;

export const PageStatLabel = styled.span`
	font-size: 0.6875rem;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.05em;
`;

export const PageStatValue = styled.span<{ tone?: "accent" | "warn" }>`
	font-size: 1rem;
	font-weight: 600;
	color: ${({ tone }) => (tone === "accent" ? "#2563eb" : tone === "warn" ? "#b45309" : "#0f172a")};
`;

// ─── Tags Cell ────────────────────────────────────────────────────────────────

export const TagsCellWrap = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	align-items: center;
`;

export const TagChip = styled(Chip)`
	&.MuiChip-root {
		height: 22px;
		font-size: 0.6875rem;
		border-radius: 4px;
		background: #e0f2fe;
		color: #0369a1;
		cursor: pointer;
	}
` as typeof Chip;

export const TagAddBtn = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	border: 1px dashed #94a3b8;
	border-radius: 4px;
	background: transparent;
	color: #94a3b8;
	font-size: 0.875rem;
	cursor: pointer;
	padding: 0;

	&:hover {
		border-color: #2563eb;
		color: #2563eb;
	}
`;

export const TagMoreChip = styled.span`
	font-size: 0.6875rem;
	color: #64748b;
	padding: 2px 6px;
	cursor: pointer;

	&:hover {
		color: #2563eb;
	}
`;

// ─── Validation Badge ─────────────────────────────────────────────────────────

export const ValidationBadgeWrap = styled.span<{ severity: "error" | "warn" | "none" }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	font-size: 0.6875rem;
	background: ${({ severity }) =>
		severity === "error" ? "#fee2e2" : severity === "warn" ? "#fef9c3" : "transparent"};
	color: ${({ severity }) => (severity === "error" ? "#dc2626" : severity === "warn" ? "#b45309" : "transparent")};
	cursor: default;
`;

// ─── Bulk Action Bar ──────────────────────────────────────────────────────────

export const BulkBarWrap = styled.div`
	position: fixed;
	bottom: 32px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1300;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 20px;
	background: #1e293b;
	border-radius: 14px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
	color: #f8fafc;
	font-size: 0.8125rem;
	white-space: nowrap;
`;

export const BulkBarLabel = styled.span`
	font-weight: 600;
	margin-right: 4px;
`;

export const BulkBarBtn = styled.button`
	padding: 6px 12px;
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 8px;
	background: transparent;
	color: #f8fafc;
	font-size: 0.8125rem;
	cursor: pointer;
	transition: background 0.15s;

	&:hover {
		background: rgba(255, 255, 255, 0.1);
	}
`;

export const BulkBarDanger = styled(BulkBarBtn)`
	border-color: rgba(239, 68, 68, 0.4);
	color: #fca5a5;

	&:hover {
		background: rgba(239, 68, 68, 0.15);
	}
`;

export const BulkBarDivider = styled.span`
	width: 1px;
	height: 20px;
	background: rgba(255, 255, 255, 0.15);
	margin: 0 4px;
`;

// ─── Detection Inbox ──────────────────────────────────────────────────────────

export const DetectionRail = styled.aside`
	position: sticky;
	top: 88px;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
	overflow: hidden;
`;

export const DetectionRailHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	border-bottom: 1px solid #e2e8f0;
	font-weight: 600;
	font-size: 0.875rem;
	color: #1e293b;
`;

export const DetectionBadge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 20px;
	height: 20px;
	padding: 0 6px;
	border-radius: 10px;
	background: #ef4444;
	color: #fff;
	font-size: 0.6875rem;
	font-weight: 700;
`;

export const DetectionList = styled.div`
	max-height: 480px;
	overflow-y: auto;
`;

export const DetectionItem = styled.div`
	padding: 10px 16px;
	border-bottom: 1px solid #f1f5f9;
	font-size: 0.8125rem;

	&:last-child {
		border-bottom: none;
	}
`;

export const DetectionText = styled.p`
	margin: 0 0 4px;
	color: #1e293b;
	font-weight: 500;
	word-break: break-word;
`;

export const DetectionMeta = styled.p`
	margin: 0 0 8px;
	color: #64748b;
	font-size: 0.75rem;
`;

export const DetectionActions = styled.div`
	display: flex;
	gap: 6px;
`;

export const DetectionActionBtn = styled.button<{ variant?: "primary" | "ghost" }>`
	padding: 4px 10px;
	border: 1px solid ${({ variant }) => (variant === "primary" ? "#2563eb" : "#e2e8f0")};
	border-radius: 6px;
	background: ${({ variant }) => (variant === "primary" ? "#2563eb" : "transparent")};
	color: ${({ variant }) => (variant === "primary" ? "#fff" : "#64748b")};
	font-size: 0.75rem;
	cursor: pointer;
	transition: all 0.15s;

	&:hover {
		background: ${({ variant }) => (variant === "primary" ? "#1d4ed8" : "#f1f5f9")};
	}
`;

// ─── Glossary Panel ───────────────────────────────────────────────────────────

export const GlossaryPanelWrap = styled.div`
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
	overflow: hidden;
`;

export const GlossaryPanelHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	border-bottom: 1px solid #e2e8f0;
	font-weight: 600;
	font-size: 0.875rem;
	color: #1e293b;
`;

export const GlossaryList = styled.div`
	max-height: 360px;
	overflow-y: auto;
`;

export const GlossaryRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 16px;
	border-bottom: 1px solid #f1f5f9;
	font-size: 0.8125rem;

	&:last-child {
		border-bottom: none;
	}
`;

export const GlossaryTerm = styled.span`
	color: #dc2626;
	font-family: monospace;
	min-width: 80px;
`;

export const GlossaryArrow = styled.span`
	color: #94a3b8;
`;

export const GlossaryCanonical = styled.span`
	color: #15803d;
	font-family: monospace;
	flex: 1;
`;

export const GlossaryNote = styled.span`
	color: #64748b;
	font-size: 0.75rem;
	flex: 1;
	font-style: italic;
`;

export const GlossaryIconBtn = styled.button`
	padding: 3px 6px;
	border: none;
	background: transparent;
	color: #94a3b8;
	cursor: pointer;
	border-radius: 4px;
	font-size: 0.875rem;

	&:hover {
		color: #ef4444;
		background: #fee2e2;
	}
`;

export const GlossaryAddWrap = styled.div`
	padding: 10px 16px;
	border-top: 1px solid #e2e8f0;
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
`;

// ─── Edit Drawer ──────────────────────────────────────────────────────────────

export const StyledDrawer = styled(Drawer)`
	z-index: 1090 !important;

	.MuiDrawer-paper {
		width: min(440px, 90vw);
		top: 64px;
		height: calc(100% - 64px);
		padding: 0;
		background: #fff;
		box-shadow: -8px 0 24px rgba(15, 23, 42, 0.08);
		border-left: 1px solid #e2e8f0;

		@media (max-width: 600px) {
			width: 100vw;
			top: 56px;
			height: calc(100% - 56px);
		}
	}

	.MuiBackdrop-root {
		top: 64px;
		background: rgba(15, 23, 42, 0.18);

		@media (max-width: 600px) {
			top: 56px;
		}
	}
`;

export const DrawerHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 20px;
	border-bottom: 1px solid #e2e8f0;
`;

export const DrawerTitleGroup = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

export const DrawerKeyBreadcrumb = styled.div`
	font-size: 0.6875rem;
	color: #94a3b8;
	font-family: "Fira Code", "Courier New", monospace;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	letter-spacing: 0.02em;
`;

export const DrawerTitle = styled.h2`
	margin: 0;
	font-size: 1rem;
	font-weight: 700;
	color: #0f172a;
	font-family: "Fira Code", "Courier New", monospace;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	letter-spacing: -0.01em;
`;

export const DrawerStatusRow = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 0.6875rem;
	color: #64748b;
`;

export const DrawerDefaultBlock = styled.div`
	margin: 12px 20px 0;
	padding: 10px 12px;
	background: #f8fafc;
	border: 1px dashed #cbd5e1;
	border-radius: 8px;
	font-size: 0.8125rem;
	color: #334155;
	line-height: 1.45;

	strong {
		display: block;
		font-size: 0.625rem;
		font-weight: 700;
		color: #64748b;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		margin-bottom: 4px;
	}
`;

export const DrawerFooter = styled.div`
	display: flex;
	gap: 8px;
	justify-content: flex-end;
	padding: 12px 20px;
	border-top: 1px solid #e2e8f0;
	background: #fff;
	position: sticky;
	bottom: 0;
`;

export const DrawerFooterBtn = styled.button<{ primary?: boolean; danger?: boolean }>`
	padding: 7px 16px;
	border-radius: 8px;
	border: 1px solid ${({ primary, danger }) => (primary ? "#2563eb" : danger ? "#dc2626" : "#e2e8f0")};
	background: ${({ primary, danger }) => (primary ? "#2563eb" : danger ? "#fff" : "#fff")};
	color: ${({ primary, danger }) => (primary ? "#fff" : danger ? "#dc2626" : "#374151")};
	font-size: 0.8125rem;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.15s;

	&:hover:not(:disabled) {
		background: ${({ primary, danger }) => (primary ? "#1d4ed8" : danger ? "#fef2f2" : "#f8fafc")};
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

export const DrawerCloseBtn = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: none;
	background: transparent;
	color: #94a3b8;
	cursor: pointer;
	border-radius: 6px;
	font-size: 1rem;

	&:hover {
		background: #f1f5f9;
		color: #374151;
	}
`;

export const DrawerTabBar = styled.div`
	display: flex;
	border-bottom: 1px solid #e2e8f0;
	background: #f8fafc;
`;

export const DrawerTabBtn = styled.button<{ active?: boolean }>`
	flex: 1;
	padding: 10px 4px;
	border: none;
	border-bottom: 2px solid ${({ active }) => (active ? "#2563eb" : "transparent")};
	background: transparent;
	color: ${({ active }) => (active ? "#2563eb" : "#64748b")};
	font-size: 0.8125rem;
	font-weight: ${({ active }) => (active ? 600 : 400)};
	cursor: pointer;
	transition: all 0.15s;

	&:hover {
		color: #2563eb;
	}
`;

export const DrawerBody = styled.div`
	padding: 16px 20px;
	overflow-y: auto;
	flex: 1;
`;

export const DrawerSection = styled.div`
	margin-bottom: 16px;
`;

export const DrawerLabel = styled.label`
	display: block;
	font-size: 0.75rem;
	font-weight: 600;
	color: #64748b;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin-bottom: 6px;
`;

export const DrawerPreviewBox = styled.div`
	padding: 12px 14px;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	background: #f8fafc;
	min-height: 40px;
	font-size: 0.875rem;
	color: #1e293b;
	line-height: 1.5;
`;

// ─── History Tab ──────────────────────────────────────────────────────────────

export const HistoryList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export const HistoryCard = styled.div`
	padding: 10px 12px;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	background: #f8fafc;
	font-size: 0.8125rem;
`;

export const HistoryMeta = styled.p`
	margin: 0 0 6px;
	color: #64748b;
	font-size: 0.75rem;
`;

export const HistoryText = styled.p`
	margin: 0 0 8px;
	color: #1e293b;
	word-break: break-word;
`;

export const HistoryRevertBtn = styled.button`
	padding: 4px 10px;
	border: 1px solid #2563eb;
	border-radius: 6px;
	background: transparent;
	color: #2563eb;
	font-size: 0.75rem;
	cursor: pointer;
	transition: all 0.15s;

	&:hover {
		background: #eff6ff;
	}
`;

// ─── Feedback Toast ───────────────────────────────────────────────────────────

export const FeedbackToast = styled.div<{ kind: "success" | "error" }>`
	position: fixed;
	bottom: 24px;
	right: 24px;
	z-index: 1400;
	padding: 12px 20px;
	border-radius: 10px;
	font-size: 0.875rem;
	font-weight: 500;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
	background: ${({ kind }) => (kind === "success" ? "#dcfce7" : "#fee2e2")};
	color: ${({ kind }) => (kind === "success" ? "#166534" : "#991b1b")};
	border: 1px solid ${({ kind }) => (kind === "success" ? "#bbf7d0" : "#fecaca")};
	display: flex;
	align-items: center;
	gap: 8px;
`;

// ─── Empty State ──────────────────────────────────────────────────────────────

export const EmptyStateWrap = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	color: #94a3b8;
	text-align: center;
`;

export const EmptyStateIcon = styled.div`
	font-size: 2.5rem;
	margin-bottom: 12px;
	opacity: 0.5;
`;

export const EmptyStateTitle = styled.h3`
	margin: 0 0 6px;
	font-size: 1rem;
	color: #475569;
`;

export const EmptyStateBody = styled.p`
	margin: 0;
	font-size: 0.875rem;
	max-width: 320px;
`;

// ─── Skeleton ─────────────────────────────────────────────────────────────────

export const SkeletonRow = styled.div`
	height: 40px;
	background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
	background-size: 200% 100%;
	animation: shimmer 1.4s infinite;
	border-radius: 6px;
	margin-bottom: 4px;

	@keyframes shimmer {
		0% {
			background-position: 200% 0;
		}
		100% {
			background-position: -200% 0;
		}
	}
`;

// ─── Publish Confirm Dialog ───────────────────────────────────────────────────

export const DialogOverlay = styled.div`
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.5);
	z-index: 1300;
	display: flex;
	align-items: center;
	justify-content: center;
`;

export const DialogBox = styled.div`
	background: #fff;
	border-radius: 16px;
	padding: 28px 32px;
	width: min(480px, calc(100vw - 32px));
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

export const DialogTitle = styled.h2`
	margin: 0 0 10px;
	font-size: 1.125rem;
	color: #1e293b;
`;

export const DialogBody = styled.p`
	margin: 0 0 24px;
	font-size: 0.9375rem;
	color: #475569;
	line-height: 1.5;
`;

export const DialogActions = styled.div`
	display: flex;
	gap: 10px;
	justify-content: flex-end;
`;

// ─── Pagination ───────────────────────────────────────────────────────────────

export const PaginationWrap = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 16px;
	border-top: 1px solid #e2e8f0;
	font-size: 0.8125rem;
	color: #64748b;
`;

export const PaginationInfo = styled.span``;

export const PaginationBtns = styled.div`
	display: flex;
	gap: 4px;
`;

export const PaginationBtn = styled.button<{ active?: boolean }>`
	padding: 4px 10px;
	border: 1px solid ${({ active }) => (active ? "#2563eb" : "#e2e8f0")};
	border-radius: 6px;
	background: ${({ active }) => (active ? "#2563eb" : "#fff")};
	color: ${({ active }) => (active ? "#fff" : "#374151")};
	font-size: 0.8125rem;
	cursor: pointer;
	transition: all 0.15s;

	&:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}

	&:hover:not(:disabled) {
		background: ${({ active }) => (active ? "#1d4ed8" : "#f8fafc")};
	}
`;

// ─── Context / Deeplink ───────────────────────────────────────────────────────

export const ContextLinkBtn = styled.a`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px;
	border: 1px solid #2563eb;
	border-radius: 8px;
	background: #eff6ff;
	color: #2563eb;
	font-size: 0.875rem;
	font-weight: 500;
	text-decoration: none;
	cursor: pointer;
	transition: all 0.15s;

	&:hover {
		background: #dbeafe;
	}
`;

// ─── Inline Edit ──────────────────────────────────────────────────────────────

export const InlineEditWrap = styled.div`
	position: relative;
	min-width: 160px;
	max-width: 320px;

	textarea {
		width: 100%;
		padding: 4px 6px;
		border: 1px solid #cbd5e1;
		border-radius: 6px;
		font-size: 0.8125rem;
		resize: none;
		font-family: inherit;
		background: #f8fafc;
		transition: border-color 0.15s;

		&:focus {
			outline: none;
			border-color: #2563eb;
			background: #fff;
		}
	}
`;
